// Polar math for tachograph chart discs.
// 1:1 with WPF TachographChartActivity.RefreshActivityStartPoint:
//   x = cx - r * cos(2π · minute / 1440)
//   y = cy - r * sin(2π · minute / 1440)
// Which places:
//   minute 0  (00:00 midnight) → LEFT  (9 o'clock)
//   minute 360 (06:00)         → TOP   (12 o'clock)
//   minute 720 (12:00 noon)    → RIGHT (3 o'clock)
//   minute 1080 (18:00)        → BOTTOM (6 o'clock)
// Time progresses CLOCKWISE. Midnight (00:00) sits OPPOSITE the teardrop on
// the chart card, which conventionally points right (toward noon at 12 RIGHT).
// In Konva's screen-angle convention (0° = right, 90° = down, 180° = left,
// 270° = up; clockwise positive), minute 0 corresponds to angle π (180°).

export const MINUTES_PER_DAY = 24 * 60

export type Polar = { angleRad: number; radius: number }
export type Point = { x: number; y: number }

export function minuteToAngleRad(minute: number): number {
  return Math.PI + (2 * Math.PI * minute) / MINUTES_PER_DAY
}

export function angleRadToMinute(angle: number): number {
  let m = ((angle - Math.PI) * MINUTES_PER_DAY) / (2 * Math.PI)
  m = ((m % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY
  return m
}

export function polarToXY(center: Point, polar: Polar): Point {
  return {
    x: center.x + polar.radius * Math.cos(polar.angleRad),
    y: center.y + polar.radius * Math.sin(polar.angleRad),
  }
}

export function xyToPolar(center: Point, p: Point): Polar {
  const dx = p.x - center.x
  const dy = p.y - center.y
  return { angleRad: Math.atan2(dy, dx), radius: Math.sqrt(dx * dx + dy * dy) }
}

// Chart timestamps are NAIVE wall-clock (no timezone), matching the rest of the
// app: activities are stored and served as local DateTimes WITHOUT a zone and
// rendered elsewhere with `moment(naiveString)` (= local). To keep the editor's
// internal minute math in one consistent frame we read a naive string's literal
// Y-M-D H:M:S as if it were UTC, so getUTC* accessors return the exact
// wall-clock. A string already carrying a zone (Z or ±HH:MM) is left untouched.
//
// This is the fix for activities spilling onto the next day: previously the
// editor emitted `Date.toISOString()` (a real UTC `…Z` instant), so an activity
// placed at 23:00 was sent as 23:00Z and re-rendered by the timeline's
// `moment()` in local time (+1/+2h) — pushing it past midnight into the next
// calendar day. Emitting a naive string keeps the literal wall-clock end-to-end.
export function parseChartIso(iso: string): Date {
  return new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`)
}

// Inverse of parseChartIso: emit `YYYY-MM-DDTHH:mm:ss` (no zone) so the value
// the server stores is the literal wall-clock, never shifted by a UTC↔local
// conversion downstream.
export function toChartIso(d: Date): string {
  return d.toISOString().slice(0, 19)
}

export function minuteToISO(dayStartIso: string, minute: number): string {
  const base = parseChartIso(dayStartIso)
  base.setUTCMinutes(base.getUTCMinutes() + Math.round(minute))
  return toChartIso(base)
}

export function isoToMinute(dayStartIso: string, iso: string): number {
  const base = parseChartIso(dayStartIso).getTime()
  const t = parseChartIso(iso).getTime()
  return Math.round((t - base) / 60000)
}
